import { WikiLayout } from "@/components/WikiLayout";
import { WikiPage } from "@/components/WikiPage";
import { AlertTriangle, Lightbulb, Gauge, Clock, Volume2 } from "lucide-react";

const Circulation = () => {
  return (
    <WikiLayout>
      <WikiPage 
        title="Circulation Basics" 
        description="Pump run times, pressure readings, and keeping water moving"
      >
        <section className="space-y-8">
          {/* Why Circulation Matters */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Why Circulation Matters</h2>
            <p className="text-muted-foreground">
              Circulation is the heartbeat of the pool system. Without proper water movement, chemicals can't 
              distribute evenly, debris can't reach the skimmer, and dead spots become breeding grounds for algae. 
              A pool with perfect chemistry but poor circulation will still develop problems.
            </p>
          </div>

          {/* Run Times */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Clock className="h-6 w-6 text-primary" />
              Pump Run Times
            </h2>
            <div className="bg-card border rounded-lg p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-foreground mb-3">Minimum Guidelines</h4>
                  <ul className="space-y-2 text-muted-foreground">
                    <li>• <strong className="text-foreground">8-12 hours/day</strong> minimum for most pools</li>
                    <li>• <strong className="text-foreground">12+ hours</strong> in summer heat</li>
                    <li>• <strong className="text-foreground">24 hours</strong> when treating problems (algae, cloudy water)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-3">Turnover Rate</h4>
                  <p className="text-muted-foreground text-sm">
                    All pool water should pass through the filter at least once every 8-12 hours. 
                    Larger pools or pools with heavy use may need more.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Variable Speed Pumps */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Variable Speed Pumps</h2>
            <p className="text-muted-foreground mb-4">
              Most BlueLux clients have variable speed pumps (Pentair IntelliFlo, Hayward VS, Jandy ePump). 
              These offer significant advantages when programmed correctly.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Low Speed</h4>
                <p className="text-sm text-muted-foreground">
                  1000-1500 RPM for overnight filtration. Quiet, energy-efficient, adequate for maintaining 
                  clean water.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">Medium Speed</h4>
                <p className="text-sm text-muted-foreground">
                  2000-2500 RPM for daily operation. Good flow for skimmers and chemical distribution.
                </p>
              </div>
              <div className="bg-card border rounded-lg p-4">
                <h4 className="font-semibold text-foreground mb-2">High Speed</h4>
                <p className="text-sm text-muted-foreground">
                  3000+ RPM for cleaning, spa jets, water features. Use sparingly — highest energy cost.
                </p>
              </div>
            </div>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <Lightbulb className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Pro Tip:</strong> Lower speed for longer runtime beats high speed 
                  for short runtime. Running at 1500 RPM for 12 hours filters more water and uses less electricity than 
                  3000 RPM for 4 hours.
                </p>
              </div>
            </div>
          </div>

          {/* Pressure Gauge */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Gauge className="h-6 w-6 text-primary" />
              Reading the Pressure Gauge
            </h2>
            <p className="text-muted-foreground mb-4">
              The filter pressure gauge is your primary diagnostic tool for circulation issues.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Understanding Pressure</h4>
              <ul className="space-y-3 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Baseline pressure:</strong> Note the PSI right after cleaning 
                  the filter. This is "clean" pressure for this specific system.
                </li>
                <li>
                  <strong className="text-foreground">Normal rise:</strong> Pressure increases as the filter catches 
                  debris. This is expected.
                </li>
                <li>
                  <strong className="text-foreground">Clean filter when:</strong> Pressure rises 8-10 PSI above baseline.
                </li>
              </ul>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4">
                <h4 className="font-semibold text-destructive mb-2">High Pressure Warning</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Dirty filter (most common)</li>
                  <li>• Obstruction in return line</li>
                  <li>• Closed or partially closed valve</li>
                  <li>• Clogged impeller (rare)</li>
                </ul>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <h4 className="font-semibold text-amber-600 mb-2">Low Pressure Warning</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Clogged skimmer or pump basket</li>
                  <li>• Air leak on suction side</li>
                  <li>• Low water level</li>
                  <li>• Pump not primed properly</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Jet Positioning */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Return Jet Positioning</h2>
            <p className="text-muted-foreground mb-4">
              Properly aimed jets create circulation patterns that push debris toward the skimmer and 
              eliminate dead spots.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-3 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Point away from skimmer:</strong> Jets should push water 
                  in a circular pattern that flows debris toward the skimmer, not away from it.
                </li>
                <li>
                  <strong className="text-foreground">Angle downward slightly:</strong> Creates circulation 
                  throughout the water column, not just at the surface.
                </li>
                <li>
                  <strong className="text-foreground">Target dead spots:</strong> Corners, behind ladders, and 
                  areas with poor flow need jet assistance.
                </li>
                <li>
                  <strong className="text-foreground">Consistent direction:</strong> All jets should work together 
                  to create one circular flow pattern.
                </li>
              </ul>
            </div>
          </div>

          {/* Baskets */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4">Skimmer & Pump Baskets</h2>
            <p className="text-muted-foreground mb-4">
              Baskets are the first line of debris defense. Clogged baskets restrict flow and can damage the pump.
            </p>
            <div className="bg-card border rounded-lg p-6">
              <h4 className="font-semibold text-foreground mb-3">Every Visit</h4>
              <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
                <li>Empty skimmer basket(s) — check all skimmers</li>
                <li>Check pump basket through the lid (clear lid) or open and empty</li>
                <li>Look for damage — cracked baskets let debris through</li>
                <li>Ensure baskets are seated properly before closing</li>
              </ol>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Caution:</strong> Always turn off the pump before opening 
                  the pump basket lid. Running the pump dry, even briefly, can damage seals and the impeller.
                </p>
              </div>
            </div>
          </div>

          {/* What to Watch For */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              <Volume2 className="h-6 w-6 text-primary" />
              What to Watch For
            </h2>
            <div className="bg-card border rounded-lg p-6">
              <ul className="space-y-3 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Air bubbles in pump:</strong> Indicates suction-side air leak. 
                  Check pump lid O-ring, unions, and water level.
                </li>
                <li>
                  <strong className="text-foreground">Pump running but no flow:</strong> Pump not primed. 
                  May need to manually prime or check for blockage.
                </li>
                <li>
                  <strong className="text-foreground">Cavitation (growling pump):</strong> Starving for water. 
                  Usually clogged basket or low water level.
                </li>
                <li>
                  <strong className="text-foreground">Thermal shutdown:</strong> Pump getting too hot. 
                  Usually from running dry or restricted flow.
                </li>
                <li>
                  <strong className="text-foreground">Unusual vibration:</strong> Could indicate failing bearings 
                  or loose mounting.
                </li>
              </ul>
            </div>
          </div>

          {/* Why It Matters */}
          <div className="bg-card border-2 border-primary rounded-lg p-6">
            <h3 className="text-lg font-semibold text-primary mb-3">Why This Matters</h3>
            <p className="text-muted-foreground">
              Good circulation means chemicals work effectively, debris gets filtered out, and the entire 
              pool volume is treated consistently. When clients complain about algae in corners or cloudy 
              water that won't clear, circulation is often the root cause — even if chemistry looks perfect.
            </p>
          </div>
        </section>
      </WikiPage>
    </WikiLayout>
  );
};

export default Circulation;